import { useEffect, useState, useCallback } from "react";
import { applyDynamicManifest } from "@/pwa/dynamicManifest";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { BrowserRouter, useLocation } from "react-router-dom";
import { ShieldCheck, Sun, LayoutDashboard, Users, Settings as SettingsIcon, Target, CalendarCheck, HeartHandshake, LogOut, Loader2, Receipt, AlertTriangle, MessageSquare } from "lucide-react";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { ThemeProvider, useTheme } from "@/theme/ThemeContext";
import Login from "@/pages/Login";
import ParentPortal from "@/portal/ParentPortal";
import Families from "@/components/Families";
import PlatformConsole from "@/components/PlatformConsole";
import Activities from "@/components/Activities";
import IncidentsPage from "@/components/OpsExtras";
import Team from "@/components/Team";
import KioskApp from "@/pages/KioskApp";
import ProviderPage from "@/pages/ProviderPage";
import Explore from "@/pages/Explore";
import Landing from "@/pages/Landing";
import Legal, { AcceptTerms, LegalLinks } from "@/pages/Legal";
import SchoolIT from "@/pages/SchoolIT";
import PoliciesPage from "@/pages/PoliciesPage";
import { providerPageName } from "@/lib/reserved";
import Signup from "@/pages/Signup";
import { BrandMark, useBrandTheme } from "@/components/Brand";
import Finance from "@/components/Finance";
import { SignPage } from "@/components/Signing";
import SetPassword from "@/pages/SetPassword";
import TrainingPortal from "@/pages/TrainingPortal";
import Dashboard from "@/components/Dashboard";
import Today from "@/components/Today";
import Settings from "@/components/Settings";
import Forecast from "@/components/Forecast";
import Messages from "@/components/Messages";
import { useSites } from "@/hooks/use-sites";
import { MapPin, ChevronDown, CalendarDays } from "lucide-react";
import CalendarView from "@/components/CalendarView";
import { isFloorStaff } from "@/lib/roles";
import NotificationsBell from "@/components/NotificationsBell";
import { takeFromLocation, parseOpen } from "@/lib/deeplink";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// The finance year screens open on: this year in New Zealand (matches the server's current_year()).
const NZ_YEAR = Number(new Intl.DateTimeFormat("en-NZ", { timeZone: "Pacific/Auckland", year: "numeric" }).format(new Date()));

// Tabs the "staff" login doesn't get: money (dashboard, finance, forecast) and owner settings.
const STAFF_HIDDEN_TABS = ["dashboard", "finance", "forecast", "settings"];

const NAV = [
  { id: "today", label: "Today", icon: Sun, testId: "nav-tab-today" },
  { id: "calendar", label: "Calendar", icon: CalendarDays, testId: "nav-tab-calendar" },
  { id: "dashboard", label: "Annual Dashboard", icon: LayoutDashboard, testId: "nav-tab-annual-dashboard" },
  { id: "activities", label: "Activities", icon: CalendarCheck, testId: "nav-tab-activities" },
  { id: "families", label: "Families", icon: HeartHandshake, testId: "nav-tab-families" },
  { id: "finance", label: "Finance", icon: Receipt, testId: "nav-tab-finance" },
  { id: "forecast", label: "Forecast", icon: Target, testId: "nav-tab-forecast" },
  { id: "messages", label: "Messages", icon: MessageSquare, testId: "nav-tab-messages" },
  { id: "team", label: "Team", icon: Users, testId: "nav-tab-team" },
  { id: "incidents", label: "Reporting", icon: AlertTriangle, testId: "nav-tab-incidents" },
  { id: "settings", label: "Settings", icon: SettingsIcon, testId: "nav-tab-settings" },
];

function SiteScopeSwitcher({ sites, siteScope, setSiteScope }) {
  const [open, setOpen] = useState(false);
  if (sites.length <= 1) return null;
  const current = sites.find((s) => s.id === siteScope);
  return (
    <div className="relative px-5 pb-4" data-testid="site-scope-switcher">
      <button data-testid="site-scope-btn" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 rounded-xl border border-[var(--hp-border-hi)] px-3 py-2 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]">
        <MapPin className="h-3.5 w-3.5 text-[var(--hp-profit)] shrink-0" />
        <span className="truncate">{current ? current.name : "All sites"}</span>
        <ChevronDown className={`h-3.5 w-3.5 ml-auto shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-5 right-5 top-full mt-1 z-30 rounded-xl border border-[var(--hp-border-hi)] bg-[var(--hp-card)] shadow-lg py-1 text-xs">
          <button data-testid="site-scope-option-all" onClick={() => { setSiteScope(""); setOpen(false); }} className={`w-full text-left px-3 py-2 hover:bg-[var(--hp-tint-5)] ${!siteScope ? "font-bold text-[var(--hp-profit)]" : "text-[var(--hp-heading)]"}`}>All sites (combined)</button>
          {sites.map((s) => (
            <button key={s.id} data-testid={`site-scope-option-${s.id}`} onClick={() => { setSiteScope(s.id); setOpen(false); }} className={`w-full text-left px-3 py-2 hover:bg-[var(--hp-tint-5)] ${siteScope === s.id ? "font-bold text-[var(--hp-profit)]" : "text-[var(--hp-heading)]"}`}>{s.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function OperatorApp() {
  const { user, logout, siteScope, setSiteScope, viewing, closeProvider, setViewMode } = useAuth();
  // A super admin looking inside a provider sees that provider's own branding and name; everyone else
  // (and the super admin's own Platform console) uses the login's branding.
  const branding = viewing ? viewing.branding : user.branding;
  useBrandTheme(branding);
  const { sites } = useSites(user.role === "admin" || user.role === "super_admin" ? API : null);
  const floorStaff = isFloorStaff(user);
  const NAV_VISIBLE = NAV.filter((n) => !(floorStaff && STAFF_HIDDEN_TABS.includes(n.id)));
  // Stripe / Xero / MYOB send the admin back with ?stripe_connect= (etc.) on the URL: open Settings on the tab whose card reports the result
  const returnTab = (() => { const q = new URLSearchParams(window.location.search); return q.get("stripe_connect") || q.get("stripe_test") || q.get("xero") || q.get("myob") ? "accounting" : q.get("gmail") ? "email" : undefined; })();
  const [asked] = useState(() => takeFromLocation());          // a tapped notification: ?go=incidents/incidents
  const askedGo = (() => { const [m, s] = (asked.go || "").split("/"); return m && NAV_VISIBLE.some((n) => n.id === m) ? [m, s || undefined] : null; })();
  const [active, setActive] = useState(askedGo ? askedGo[0] : (window.location.pathname.startsWith("/billing") || returnTab) && !isFloorStaff(user) ? "settings" : "today");
  // the sub-tab to open when a shortcut (e.g. on the Today page) jumps into another page
  const [subTab, setSubTab] = useState(askedGo ? askedGo[1] : returnTab);
  const [navKey, setNavKey] = useState(0);        // goes up when a tapped notification moves the app to another screen, so a page already showing starts again on the right sub-tab
  useEffect(() => {                                           // the app was already open when a notification was tapped
    const h = (e) => {
      const [m, s] = (parseOpen(e.detail).go || "").split("/");
      if (m && NAV.some((n) => n.id === m) && !(isFloorStaff(user) && STAFF_HIDDEN_TABS.includes(m))) { setSubTab(s || undefined); setActive(m); setNavKey((k) => k + 1); window.scrollTo({ top: 0 }); }
    };
    window.addEventListener("hp-open", h); return () => window.removeEventListener("hp-open", h);
  }, [user]);
  const goTo = (mainId, sub) => { if (!(isFloorStaff(user) && STAFF_HIDDEN_TABS.includes(mainId))) { setSubTab(sub); setActive(mainId); } };
  const [year, setYear] = useState(NZ_YEAR);
  const [years, setYears] = useState([NZ_YEAR]);
  const [months, setMonths] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [staff, setStaff] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const loadSettings = useCallback(async (yr) => {
    try {
      const res = await axios.get(`${API}/settings`, { params: { year: yr ?? year } });
      setSettings(res.data);
    } catch (e) { console.error(e); }
  }, [year]);

  const loadStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/staff`);
      setStaff(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const loadData = useCallback(async (yr) => {
    const y = yr ?? year;
    if (floorStaff) { setLoading(false); return; }  // income / profit figures: not for the staff login
    try {
      const [m, s] = await Promise.all([
        axios.get(`${API}/months`, { params: { year: y } }),
        axios.get(`${API}/summary`, { params: { year: y } }),
      ]);
      setMonths(m.data);
      setSummary(s.data);
    } catch (e) {
      console.error("Failed to load data", e);
    } finally {
      setLoading(false);
    }
  }, [year, floorStaff]);

  const loadYears = useCallback(async () => {
    if (floorStaff) return;
    try {
      const res = await axios.get(`${API}/years`);
      setYears(res.data);
    } catch (e) {
      console.error(e);
    }
  }, [floorStaff]);

  useEffect(() => { loadYears(); }, [loadYears]);
  useEffect(() => { loadStaff(); }, [loadStaff, siteScope]);
  // Settings now includes the Fee Schedule, which is per-site (#158) -- re-fetch on a site switch
  // too, or the Fees tab would keep showing whichever site's schedule loaded last.
  useEffect(() => { loadData(year); loadSettings(year); }, [year, loadData, loadSettings, siteScope]);

  const loadUnreadMessages = useCallback(() => {
    axios.get(`${API}/admin-messages/unread-count`).then((r) => setUnreadMessages(r.data.unread)).catch(() => {});
  }, []);
  useEffect(() => {
    // The red count on Messages: checked every 20 seconds while the page is showing, as soon as the person comes back to it, and the moment a notification arrives
    const now = () => { if (document.visibilityState !== "hidden") loadUnreadMessages(); };
    loadUnreadMessages();
    const id = setInterval(now, 20000);
    document.addEventListener("visibilitychange", now); window.addEventListener("focus", now); window.addEventListener("hp-push", loadUnreadMessages);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", now); window.removeEventListener("focus", now); window.removeEventListener("hp-push", loadUnreadMessages); };
  }, [loadUnreadMessages]);

  const changeYear = (y) => { setLoading(true); setYear(y); };

  const addYear = async (y) => {
    const res = await axios.post(`${API}/years/${y}`);
    setYears(res.data.years);
    changeYear(y);
  };

  const removeYear = async (y) => {
    const res = await axios.delete(`${API}/years/${y}`);
    setYears(res.data.years);
    if (y === year) changeYear(res.data.years[res.data.years.length - 1]);
    return res.data.years;
  };

  return (
    <div className="App grain min-h-screen bg-[var(--hp-bg)]">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="hidden md:flex w-[260px] flex-col border-r border-[var(--hp-border)] bg-[var(--hp-sidebar)] fixed h-screen z-20">
          <div className="px-6 py-7 border-b border-[var(--hp-border)]">
            <BrandMark branding={branding} />
          </div>
          {(user.role === "admin" || user.role === "super_admin") && (
            <div className="pt-4 border-b border-[var(--hp-border)]">
              <SiteScopeSwitcher sites={sites} siteScope={siteScope} setSiteScope={setSiteScope} />
            </div>
          )}

          <nav className="flex-1 px-4 py-6 space-y-1.5">
            {NAV_VISIBLE.map((n) => {
              const Icon = n.icon;
              const isActive = active === n.id;
              return (
                <button
                  key={n.id}
                  data-testid={n.testId}
                  onClick={() => goTo(n.id)}
                  className={`relative w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors duration-200 ${
                    isActive
                      ? "bg-[var(--hp-primary)]/12 text-[var(--hp-profit)]"
                      : "text-[var(--hp-muted)] hover:text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]"
                  }`}
                >
                  {isActive && (
                    <motion.span layoutId="nav-indicator"
                      className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r bg-[var(--hp-primary)]" />
                  )}
                  <Icon className="h-[18px] w-[18px]" />
                  {n.label}
                  {n.id === "messages" && unreadMessages > 0 && <span data-testid="nav-messages-badge" aria-label={`${unreadMessages} unread ${unreadMessages === 1 ? "message" : "messages"}`} className="ml-auto min-w-[1.5rem] rounded-full bg-[var(--hp-loss)] px-2 py-0.5 text-center text-xs font-bold text-white">{unreadMessages > 99 ? "99+" : unreadMessages}</span>}
                </button>
              );
            })}
          </nav>

          <div className="px-5 py-3 border-t border-[var(--hp-border)] flex items-center gap-2 text-xs">
            <div className="min-w-0 flex-1"><p className="truncate font-semibold text-[var(--hp-heading)]" data-testid="sidebar-user-name">{user.name}</p><p className="truncate text-xs uppercase tracking-wider text-[var(--hp-muted)]">{user.role.replace("_", " ")}{(user.tenant_name || viewing?.name) ? ` · ${user.tenant_name || viewing?.name}` : ""}</p></div>
            <NotificationsBell />
            <button data-testid="sidebar-logout" onClick={logout} title="Sign out" className="grid h-8 w-8 place-items-center rounded-full border border-[var(--hp-border-hi)] text-[var(--hp-muted)] hover:text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]"><LogOut className="h-3.5 w-3.5" /></button>
          </div>
          <p className="px-5 pb-3 text-[11px] text-[var(--hp-muted)]" data-testid="sidebar-legal"><LegalLinks /></p>
          {summary && ["dashboard", "finance", "forecast"].includes(active) && (
            <div className="px-5 py-5 border-t border-[var(--hp-border)]">
              <p className="text-xs uppercase tracking-wider text-[var(--hp-muted)] mb-2">Net Profit {year}</p>
              <p className={`font-mono-num text-xl font-bold ${summary.net_profit >= 0 ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>
                {summary.net_profit >= 0 ? "+" : ""}{summary.net_profit.toLocaleString("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 })}
              </p>
            </div>
          )}
        </aside>

        {/* Mobile top nav */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50 glass border-b border-[var(--hp-border)] flex items-center gap-2 pl-3 pr-2 py-2">
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto" data-testid="mobile-nav-tabs">
          {NAV_VISIBLE.map((n) => {
            const Icon = n.icon;
            const isActive = active === n.id;
            return (
              <button key={n.id} data-testid={`${n.testId}-mobile`} onClick={() => goTo(n.id)}
                className={`relative flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-2 text-xs font-semibold ${isActive ? "bg-[var(--hp-primary)] text-[#04231a]" : "text-[var(--hp-muted)] bg-[var(--hp-tint-5)]"}`}>
                <Icon className="h-4 w-4" /> {n.label}
                {n.id === "messages" && unreadMessages > 0 && <span data-testid="nav-messages-badge-mobile" className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-[var(--hp-loss)] animate-pulse" />}
              </button>
            );
          })}
          </div>
          <NotificationsBell />
          <button data-testid="mobile-logout" onClick={logout} aria-label="Sign out" className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[var(--hp-border-hi)] px-3 py-2 text-xs font-semibold text-[var(--hp-heading)]"><LogOut className="h-4 w-4" /><span className="hidden min-[460px]:inline">Sign out</span></button>
        </div>

        {/* Main content */}
        <main className="flex-1 min-w-0 md:ml-[260px] px-4 sm:px-8 lg:px-12 py-8 md:py-10 pt-20 md:pt-10 relative z-40 max-w-[1400px]">
          {viewing && (
            <div className={`mb-6 rounded-2xl border px-4 py-3 flex flex-wrap items-center gap-3 text-sm ${viewing.mode === "edit" ? "border-[var(--hp-gold)]/50 bg-[var(--hp-gold)]/8" : "border-[var(--hp-primary)]/40 bg-[var(--hp-primary)]/8"}`} data-testid="viewing-provider-banner" data-mode={viewing.mode}>
              <ShieldCheck className={`h-4 w-4 shrink-0 ${viewing.mode === "edit" ? "text-[var(--hp-gold)]" : "text-[var(--hp-profit)]"}`} />
              <p className="flex-1 min-w-[220px] text-[var(--hp-heading)]">{viewing.mode === "edit"
                ? <>Editing <span className="font-semibold">{viewing.name}</span> as super admin — changes are real and are recorded.</>
                : <>Viewing <span className="font-semibold">{viewing.name}</span> as super admin — <span className="font-semibold">read-only</span>: you can look, but nothing can be changed.</>}
                {viewing.access?.expiresAt && <span className="block text-xs text-[var(--hp-muted)] mt-0.5" data-testid="viewing-access-until">Allowed by {viewing.name} {viewing.access.level === "edit" ? "(look and change)" : "(look only)"} until {new Date(viewing.access.expiresAt).toLocaleString("en-NZ", { timeZone: "Pacific/Auckland", weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>}</p>
              {viewing.mode === "edit"
                ? <button data-testid="switch-to-view" onClick={() => setViewMode("view").catch(() => toast.error("Couldn't switch"))} className="rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-card)] px-3.5 py-1.5 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]">Switch to read-only</button>
                : viewing.access?.level === "view" ? <span data-testid="edit-not-allowed" className="text-xs text-[var(--hp-muted)]">{viewing.name} allowed look-only access</span>
                : <button data-testid="switch-to-edit" onClick={() => { if (window.confirm(`Edit ${viewing.name}? Changes you make are real and are recorded.`)) setViewMode("edit").catch(() => toast.error("Couldn't switch")); }} className="rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-card)] px-3.5 py-1.5 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]">Edit this provider</button>}
              <button data-testid="back-to-platform" onClick={closeProvider} className="rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-card)] px-3.5 py-1.5 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]">← Platform console</button>
            </div>
          )}
          {user.subscription?.blocked && (
            <div className="mb-6 rounded-2xl border border-[var(--hp-loss)]/50 bg-[var(--hp-loss)]/10 p-4 flex flex-wrap items-center gap-3 text-sm" data-testid="trial-lock-banner">
              <AlertTriangle className="h-5 w-5 text-[var(--hp-loss)]" /><p className="flex-1 min-w-[240px] text-[var(--hp-heading)]"><span className="font-semibold">{user.subscription.reduced ? "Your access has been reduced." : user.subscription.status === "trial_expired" ? "Your free trial has ended." : "Your subscription has lapsed."}</span>{" "}
                {user.subscription.message ? <span className="whitespace-pre-line [overflow-wrap:anywhere]" data-testid="trial-lock-message">{user.subscription.message}</span> : "Booking is locked until you subscribe: no new bookings can be made by you or your families. Everything already here stays readable, and sign-in and sign-out at the kiosk keep working."}</p>
              <button data-testid="trial-lock-subscribe" onClick={() => goTo("settings")} className="rounded-full bg-[var(--hp-primary)] px-4 py-2 text-xs font-bold text-[#04231a]">Subscribe now</button>
            </div>
          )}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${active}:${siteScope || "all"}:${navKey}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {active === "today" && <Today api={API} unreadMessages={unreadMessages} onGo={goTo} />}
              {active === "calendar" && <CalendarView api={API} />}
              {active === "dashboard" && !floorStaff && (
                <Dashboard api={API} year={year} years={years} months={months} summary={summary} settings={settings}
                  loading={loading} reload={loadData} onYearChange={changeYear} onAddYear={addYear} onDeleteYear={removeYear} onOpenRoll={() => goTo("activities", "rolls")} />
              )}
              {active === "forecast" && !floorStaff && <Forecast api={API} year={year} years={years} onYearChange={changeYear} onAddYear={addYear} settings={settings} reloadSettings={() => { loadSettings(year); loadData(year); }} initialTab={subTab} />}
              {active === "team" && <Team api={API} year={year} staff={staff} settings={settings} reloadStaff={loadStaff} initialTab={subTab} />}
              {active === "families" && <Families api={API} initialTab={subTab} />}
              {active === "activities" && <Activities api={API} settings={settings} year={year} reloadSettings={() => { loadSettings(year); loadData(year); }} initialTab={subTab} />}
              {active === "incidents" && <IncidentsPage api={API} initialTab={subTab} />}
              {active === "messages" && <Messages api={API} onRead={loadUnreadMessages} initialTab={subTab} />}
              {active === "finance" && !floorStaff && <Finance api={API} settings={settings} year={year} months={months} staff={staff} reload={loadData} reloadSettings={() => { loadSettings(year); loadData(year); }} initialTab={subTab} />}
              {active === "settings" && !floorStaff && <Settings api={API} settings={settings} year={year} reloadSettings={() => { loadSettings(year); loadData(year); }} initialTab={subTab} />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

function Gate() {
  const location = useLocation();
  const { user, viewing } = useAuth();
  useEffect(() => { applyDynamicManifest(location.pathname); }, [location.pathname]);
  // one-time links, the kiosk and the family portal are never for search results
  useEffect(() => {
    if (!/^\/(sign|invite|kiosk|family|embed|signup|billing)(\/|$)/.test(location.pathname)) return undefined;
    const m = document.createElement("meta"); m.name = "robots"; m.content = "noindex,nofollow"; document.head.appendChild(m);
    return () => m.remove();
  }, [location.pathname]);
  if (location.pathname.startsWith("/sign/")) return <SignPage token={location.pathname.split("/sign/")[1]} />;
  if (location.pathname.startsWith("/invite/")) return <SetPassword token={location.pathname.split("/invite/")[1]} />;
  if (location.pathname.startsWith("/activities/") || providerPageName(location.pathname)) return <ProviderPage />;
  if (location.pathname === "/explore" || location.pathname.startsWith("/explore/")) return <Explore />;
  if (/^\/(terms|privacy|legal)\/?$/.test(location.pathname)) return <Legal />;
  if (/^\/school-it\/?$/.test(location.pathname)) return <SchoolIT />;
  if (/^\/policies\/[^/]+\/?$/.test(location.pathname)) return <PoliciesPage />;
  if (user === null) return <div className="min-h-screen grid place-items-center bg-[var(--hp-bg)] text-[var(--hp-muted)] text-sm" data-testid="auth-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>;
  if (user === false && location.pathname === "/") return <Landing />;
  const embed = location.pathname.startsWith("/embed/");
  const family = location.pathname.startsWith("/family");
  if (location.pathname.startsWith("/kiosk/")) return <KioskApp />;
  if (location.pathname.startsWith("/signup") && (user === false || user?.role === "parent")) return <Signup />;
  if (user && user.role === "kiosk") { window.location.replace(`/kiosk/${user.tenant_slug}`); return null; }
  if (user && user.legal?.needed) return <AcceptTerms />;   // new or updated Terms / Privacy Policy: accepted before anything else
  if (user === false) return <Login embed={embed} family={family} />;
  if (user.role === "parent" || embed || family) return <ParentPortal embed={embed} family={family} />;
  // The super admin belongs to the platform, not to a provider: they start in the Platform console and
  // only enter a provider's app by opening it there.
  if (user.role === "super_admin" && !viewing) return <PlatformConsole />;
  // a staff login with required training still to do (or paused by an admin) gets the training-only portal; the server refuses everything else
  if (user.role === "staff" && user.trainingLock?.locked) return <TrainingPortal />;
  return <OperatorApp />;
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" richColors />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ThemedToaster />
          <Gate />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
